import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Map, ArrowRight, Search, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const NotFound = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute top-0 left-0 w-full h-full opacity-5" viewBox="0 0 1000 1000">
          <rect x="0" y="0" width="1000" height="1000" fill="transparent" />
          {[...Array(20)].map((_, i) => (
            <g key={i} transform={`translate(${Math.random() * 1000}, ${Math.random() * 1000})`}>
              <circle cx="0" cy="0" r={Math.random() * 10 + 1} fill="#3B82F6" />
            </g>
          ))}
          {[...Array(10)].map((_, i) => (
            <line 
              key={i}
              x1={Math.random() * 1000}
              y1={Math.random() * 1000}
              x2={Math.random() * 1000}
              y2={Math.random() * 1000}
              stroke="#3B82F6"
              strokeWidth="1"
              strokeOpacity="0.2"
            />
          ))}
        </svg>
      </div>

      <div className="relative z-10 w-full max-w-2xl px-6 py-12">
        <div className="flex flex-col items-center">
          {/* 404 Number */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 200 }}
          >
            <div className="relative text-9xl font-bold bg-clip-text text-transparent 
                           bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-500 
                           select-none pb-4">
              404
            </div>
            <motion.div 
              className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3/4 h-1 
                         bg-gradient-to-r from-transparent via-blue-500 to-transparent blur-sm"
              animate={{ width: ["60%", "80%", "60%"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>

          {/* Message */}
          <motion.div
            className="text-center mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              Página no encontrada
            </h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Parece que te has aventurado en territorio desconocido. La página que buscas no existe o ha sido movida a otra ubicación.
            </p>
          </motion.div>

          {/* Illustration */}
          <motion.div
            className="my-8 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <div className="relative">
              <div className="w-32 h-32 bg-blue-100 rounded-full flex items-center justify-center">
                <Map className="w-16 h-16 text-blue-500 opacity-70" />
              </div>
              <motion.div
                className="absolute -top-3 -right-3 bg-white rounded-full p-2 shadow-lg"
                animate={{ rotate: [0, 10, 0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              >
                <Search className="w-6 h-6 text-indigo-500" />
              </motion.div>
              <motion.div 
                className="absolute -bottom-2 -left-2 text-3xl"
                animate={{ rotate: [-10, 10, -10] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                ?
              </motion.div>
              <motion.div 
                className="absolute top-1/2 -right-8 text-3xl"
                animate={{ x: [0, 5, 0, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                !
              </motion.div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 items-center mt-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
          >
            <Link
              to="/"
              className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r 
                       from-indigo-600 to-blue-600 text-white font-medium
                       rounded-xl shadow-md transition-all duration-300"
            >
              <Home className="w-5 h-5 transition-transform group-hover:-translate-y-1" />
              <span>Volver al inicio</span>
              <motion.div
                className="w-0 h-0.5 bg-white absolute bottom-0 left-1/2 transform -translate-x-1/2"
                whileHover={{ width: "70%" }}
                transition={{ duration: 0.3 }}
              />
            </Link>
            
            <Link
              to="/map"
              className="group flex items-center gap-2 px-6 py-3 bg-white text-indigo-600 
                       border border-indigo-100 font-medium rounded-xl shadow-sm hover:shadow 
                       transition-all duration-300"
            >
              <span>Explorar propiedades</span>
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;